import React, { useState, useEffect, useCallback } from 'react';
import { PhoneCall, PhoneOff, Mic, Volume2, CheckCircle2, XCircle, Loader, Clock, RefreshCw, AlertCircle, BookOpen, Save, Trash2, Upload, Users } from 'lucide-react';
import api from '../../services/api';
import ExcelImportModal from './ExcelImportModal';

const PRODUTOS = [
  { key: 'Geral',                   label: '★ Geral',                    short: 'Geral' },
  { key: 'Ingarden',                label: 'Ingarden',                   short: 'Ingarden' },
  { key: 'Lumiere Residence Club',  label: 'Lumiere Residence Club',     short: 'Lumiere' },
  { key: 'Benjamin Constant Duplex', label: 'Benjamin Constant Duplex', short: 'Benjamin' },
];

function StatusBadge({ online }) {
  if (online === null) return (
    <span className="flex items-center gap-1.5 text-xs text-gray-400"><Loader size={12} className="animate-spin" /> Verificando...</span>
  );
  return online ? (
    <span className="flex items-center gap-1.5 text-xs text-green-600 font-semibold"><CheckCircle2 size={13} /> SDR Online</span>
  ) : (
    <span className="flex items-center gap-1.5 text-xs text-red-500 font-semibold"><XCircle size={13} /> SDR Offline</span>
  );
}

function HistoricoItem({ item }) {
  const fmt = (ts) => new Date(ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${item.sucesso ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'}`}>
        {item.sucesso ? <PhoneCall size={14} /> : <PhoneOff size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{item.nome || 'Lead'}</p>
        <p className="text-xs text-gray-400 font-mono">{item.numero}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs text-gray-500">{item.produto}</p>
        <p className="text-xs text-gray-400">{fmt(item.ts)}</p>
      </div>
    </div>
  );
}

export default function SdrVoz() {
  const [online, setOnline] = useState(null);
  const [vozes, setVozes] = useState({});
  const [vozPadrao, setVozPadrao] = useState('RGymW84CSmfVugnA5tvA');
  const [vozSelecionada, setVozSelecionada] = useState('RGymW84CSmfVugnA5tvA');
  const [numero, setNumero] = useState('');
  const [nome, setNome] = useState('');
  const [produto, setProduto] = useState('Geral');
  const [ligando, setLigando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [historico, setHistorico] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sdr_historico') || '[]'); } catch { return []; }
  });
  const [concorrencia, setConcorrencia] = useState(1);
  const [maxConcorrencia, setMaxConcorrencia] = useState(1);
  const [emLigacao, setEmLigacao] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);

  // ── Fonte de Conhecimento ─────────────────────────────────────────────────
  const [conhecimento, setConhecimento] = useState('');
  const [knStatus, setKnStatus] = useState(null); // null | 'ok' | 'vazio' | 'carregando' | 'salvando'
  const [knMsg, setKnMsg] = useState('');

  const checkStatus = useCallback(async () => {
    try {
      const r = await api.get('/sdr-voz/health');
      setOnline(r.data.data?.status === 'ok');
    } catch { setOnline(false); }
  }, []);

  const loadVozes = useCallback(async () => {
    try {
      const r = await api.get('/sdr-voz/vozes');
      if (r.data.success) {
        setVozes(r.data.data.vozes || {});
        const pad = r.data.data.padrao || 'pt_BR-faber-medium';
        setVozPadrao(pad);
        setVozSelecionada(pad);
      }
    } catch {}
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/sdr-voz/status');
      if (r.data.success) {
        setConcorrencia(r.data.data.concorrencia || 1);
        setMaxConcorrencia(r.data.data.max_concorrencia || 1);
        setEmLigacao(r.data.data.em_ligacao || 0);
      }
    } catch {}
  }, []);

  const loadConhecimento = useCallback(async (prod) => {
    setKnStatus('carregando');
    setKnMsg('');
    try {
      const r = await api.get(`/sdr-voz/knowledge/${encodeURIComponent(prod)}`);
      const txt = r.data.data?.conhecimento || '';
      setConhecimento(txt);
      setKnStatus(txt ? 'ok' : 'vazio');
      setKnMsg(txt ? `Base salva (${txt.length} caracteres)` : 'Nenhuma base definida para este empreendimento');
    } catch {
      setKnStatus('vazio');
      setKnMsg('Erro ao carregar');
    }
  }, []);

  useEffect(() => {
    checkStatus();
    loadVozes();
    loadStatus();
    loadConhecimento('Ingarden');
    const iv = setInterval(checkStatus, 30000);
    const ivStatus = setInterval(loadStatus, 10000);
    return () => {
      clearInterval(iv);
      clearInterval(ivStatus);
    };
  }, [checkStatus, loadVozes, loadStatus, loadConhecimento]);

  const handleProdutoChange = (p) => {
    setProduto(p);
    loadConhecimento(p);
  };

  const handleSalvarConhecimento = async () => {
    if (!conhecimento.trim()) return;
    setKnStatus('salvando');
    try {
      await api.post(`/sdr-voz/knowledge/${encodeURIComponent(produto)}`, { conhecimento: conhecimento.trim() });
      setKnStatus('ok');
      setKnMsg(`Base salva com sucesso (${conhecimento.trim().length} caracteres)`);
    } catch {
      setKnStatus('vazio');
      setKnMsg('Erro ao salvar');
    }
  };

  const handleLimparConhecimento = async () => {
    if (!window.confirm(`Apagar a base de conhecimento do "${produto}"?`)) return;
    try {
      await api.delete(`/sdr-voz/knowledge/${encodeURIComponent(produto)}`);
      setConhecimento('');
      setKnStatus('vazio');
      setKnMsg('Base removida');
    } catch {}
  };

  const handleLigar = async (e) => {
    e.preventDefault();
    if (!numero.trim()) { setErro('Informe o número do lead'); return; }
    if (emLigacao >= concorrencia) { setErro('Número máximo de ligações simultâneas atingido'); return; }
    setErro(''); setSucesso(''); setLigando(true);
    try {
      const r = await api.post('/sdr-voz/ligar', { numero: numero.trim(), nome: nome.trim() || 'Lead', produto, voz: vozSelecionada, conhecimento: conhecimento.trim() });
      if (r.data.success) {
        const item = { numero: numero.trim(), nome: nome.trim() || 'Lead', produto, voz: vozSelecionada, call_sid: r.data.data?.call_sid, sucesso: true, ts: Date.now() };
        const novo = [item, ...historico].slice(0, 50);
        setHistorico(novo);
        localStorage.setItem('sdr_historico', JSON.stringify(novo));
        setSucesso(`Ligando para ${numero.trim()}… Call SID: ${r.data.data?.call_sid || '-'}`);
        setNumero(''); setNome('');
        setTimeout(() => setSucesso(''), 8000);
      } else {
        setErro(r.data.error || 'Erro ao disparar ligação');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Erro desconhecido';
      setErro(typeof msg === 'string' ? msg : JSON.stringify(msg));
      const item = { numero: numero.trim(), nome: nome.trim() || 'Lead', produto, sucesso: false, ts: Date.now() };
      const novo = [item, ...historico].slice(0, 50);
      setHistorico(novo);
      localStorage.setItem('sdr_historico', JSON.stringify(novo));
    } finally {
      setLigando(false);
    }
  };

  const handleImportSuccess = (data) => {
    setHistorico(prev => [
      ...data.importados.map(lead => ({
        numero: lead.numero,
        nome: lead.nome || 'Lead',
        produto: lead.produto || 'Ingarden',
        sucesso: true,
        ts: Date.now(),
        importado: true
      })),
      ...prev
    ].slice(0, 100));
  };

  const handleConcorrenciaChange = async (value) => {
    const newValue = Math.min(Math.max(1, parseInt(value) || 1), maxConcorrencia);
    setConcorrencia(newValue);
    try {
      await api.post('/sdr-voz/configurar-concorrencia', { concorrencia: newValue });
    } catch (err) {
      setConcorrencia(1);
      setErro('Erro ao atualizar concorrência');
    }
  };

  const limparHistorico = () => {
    if (!window.confirm('Limpar todo o histórico?')) return;
    setHistorico([]);
    localStorage.removeItem('sdr_historico');
  };

  const knBorderColor = knStatus === 'ok' ? 'border-green-300 bg-green-50' : knStatus === 'vazio' ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-gray-50';
  const knTextColor   = knStatus === 'ok' ? 'text-green-700' : knStatus === 'vazio' ? 'text-yellow-700' : 'text-gray-500';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">SDR por Voz</h1>
          <p className="text-sm text-gray-500 mt-0.5">Discador automático com IA — Twilio · Whisper · Claude · ElevenLabs TTS (PT-BR nativo)</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge online={online} />
          <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
            <Users size={14} className="text-gray-600" />
            <span className="text-sm text-gray-700">
              {emLigacao}/{concorrencia} ligações
            </span>
          </div>
          <button onClick={() => setShowImportModal(true)} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50">
            <Upload size={13} /> Importar Excel
          </button>
          <button onClick={() => { checkStatus(); loadVozes(); loadStatus(); }} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50">
            <RefreshCw size={13} /> Atualizar
          </button>
        </div>
      </div>

      {online === false && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          <AlertCircle size={15} />
          <span>O servidor SDR está offline. Verifique o VPS <span className="font-mono font-semibold">sdr.omestredaspromos.com.br</span>.</span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">

        {/* ── COLUNA ESQUERDA: Ligação + Fonte de Conhecimento ── */}
        <div className="xl:col-span-3 space-y-4">

          {/* Disparar Ligação */}
          <div className="bg-white rounded-xl shadow p-5">
            <h2 className="font-semibold text-gray-700 text-sm mb-4 flex items-center gap-2">
              <PhoneCall size={16} className="text-primary" /> Nova Ligação
            </h2>
            <form onSubmit={handleLigar} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Número *</label>
                  <input value={numero} onChange={e => setNumero(e.target.value)} placeholder="+5544999999999"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome do Lead</label>
                  <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: João Silva"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Empreendimento</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {PRODUTOS.map(p => {
                    const isGeral = p.key === 'Geral';
                    const isActive = produto === p.key;
                    const baseCls = 'px-2 py-2.5 rounded-lg text-xs font-semibold border-2 transition-all';
                    const activeCls = isGeral
                      ? 'border-amber-500 bg-amber-500 text-white'
                      : 'border-primary bg-primary text-white';
                    const idleCls = isGeral
                      ? 'border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-500'
                      : 'border-gray-200 text-gray-600 hover:border-blue-300';
                    return (
                      <button key={p.key} type="button" onClick={() => handleProdutoChange(p.key)}
                        className={`${baseCls} ${isActive ? activeCls : idleCls}`}>
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Configuração de Concorrência */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  <Users size={12} className="inline mr-1" /> Ligações Simultâneas
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max={maxConcorrencia}
                    value={concorrencia}
                    onChange={(e) => handleConcorrenciaChange(e.target.value)}
                    className="w-20 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <span className="text-xs text-gray-500">Máx: {maxConcorrencia}</span>
                  <span className="text-xs text-gray-400">
                    {emLigacao > 0 && `${emLigacao} ativa${emLigacao > 1 ? 's' : ''}`}
                  </span>
                </div>
              </div>

              {Object.keys(vozes).length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5"><Mic size={12} className="inline mr-1" /> Voz do Atendente</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(vozes).map(([key, desc]) => (
                      <button key={key} type="button" onClick={() => setVozSelecionada(key)}
                        className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border-2 transition-all text-center ${vozSelecionada === key ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-blue-200'}`}>
                        <span className="text-lg">🎙️</span>
                        <span className={`text-xs font-semibold truncate w-full ${vozSelecionada === key ? 'text-primary' : 'text-gray-700'}`}>{desc.split(' (')[0]}</span>
                        <span className="leading-tight px-1.5 py-0.5 rounded-full bg-green-100 text-green-700" style={{ fontSize: '9px' }}>🇧🇷 {desc.includes('feminino') ? '♀ feminino' : '♂ masculino'}</span>
                        {key === vozPadrao && <span className="bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full" style={{ fontSize: '9px' }}>padrão</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {erro && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5 rounded-lg">
                  <XCircle size={14} /> {erro}
                </div>
              )}
              {sucesso && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2.5 rounded-lg">
                  <CheckCircle2 size={14} /> {sucesso}
                </div>
              )}

              <button type="submit" disabled={ligando || !online}
                className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {ligando ? <><Loader size={15} className="animate-spin" /> Disparando ligação...</> : <><PhoneCall size={15} /> Disparar Ligação</>}
              </button>
              {!online && online !== null && (
                <p className="text-center text-xs text-red-400">SDR offline — não é possível ligar</p>
              )}
            </form>
          </div>

          {/* Fonte de Conhecimento */}
          <div className="bg-white rounded-xl shadow p-5">
            <h2 className="font-semibold text-gray-700 text-sm mb-1 flex items-center gap-2">
              <BookOpen size={15} className="text-primary" /> Fonte de Conhecimento
              <span className={`text-xs font-normal ml-1 ${produto === 'Geral' ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                — {produto === 'Geral' ? '★ Geral (sempre injetada em toda ligação)' : produto}
              </span>
            </h2>
            <p className="text-xs text-gray-400 mb-3">
              {produto === 'Geral'
                ? 'Este texto é SEMPRE injetado no system prompt da Bruna, independente do empreendimento selecionado. Use para regras transversais e contexto que vale pra qualquer ligação.'
                : 'O consultor virtual usa este texto como base ao ligar para leads do empreendimento selecionado acima. Inclua: valores, plantas, diferenciais, condições, objeções comuns.'}
            </p>

            {knMsg && (
              <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg border mb-3 ${knBorderColor} ${knTextColor}`}>
                {knStatus === 'ok' && <CheckCircle2 size={12} />}
                {knStatus === 'vazio' && <AlertCircle size={12} />}
                {knStatus === 'carregando' && <Loader size={12} className="animate-spin" />}
                {knStatus === 'salvando' && <Loader size={12} className="animate-spin" />}
                {knMsg}
              </div>
            )}

            <textarea
              value={conhecimento}
              onChange={e => setConhecimento(e.target.value)}
              rows={8}
              placeholder={produto === 'Geral'
                ? `Texto SEMPRE injetado na ligação, independente do empreendimento.\n\nSugestões de conteúdo:\n— Lista resumida dos outros empreendimentos (1-2 frases cada, com endereço)\n— Endereço/horário geral da Inbase (Av. Brasil 2175, seg-sex 9h-18h)\n— Regra de objeção geral: "se perguntar preço, diga que depende da unidade"\n— Quando passar pro Fernando: "Quer que eu peça pro Fernando te ligar? Qual o melhor horário?"\n— Regra de pronúncia TTS: "Ingárden", "Lumi-ér", "In-báse"`
                : `Escreva aqui as informações do ${produto} para o consultor virtual...\n\nExemplos:\n— Localização, plantas disponíveis, metragens\n— Valores e condições de pagamento\n— Diferenciais do empreendimento\n— Como responder objeções comuns`}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y font-sans leading-relaxed"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">{conhecimento.length} caracteres</span>
              <div className="flex gap-2">
                <button onClick={handleLimparConhecimento} className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 rounded-lg text-xs text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 size={12} /> Limpar
                </button>
                <button onClick={handleSalvarConhecimento} disabled={!conhecimento.trim() || knStatus === 'salvando'}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-secondary disabled:opacity-50 transition-colors">
                  {knStatus === 'salvando' ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}
                  Salvar
                </button>
              </div>
            </div>
          </div>

          {/* Como funciona */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700 space-y-1.5">
            <p className="font-semibold flex items-center gap-1.5"><Volume2 size={12} /> Como funciona:</p>
            <p>1. O sistema discará automaticamente para o número informado via Twilio.</p>
            <p>2. O atendente virtual (Victor da Inbase) inicia a conversa e qualifica o lead em tempo real.</p>
            <p>3. Transcrição via Whisper STT · Resposta via Claude IA · Voz via ElevenLabs TTS — vozes nativas PT-BR (Roberta, Nina, Giselli, Marianne, Eric, Michael, Filipe).</p>
            <p>4. A Fonte de Conhecimento é usada pelo Claude em cada ligação para o empreendimento selecionado.</p>
          </div>
        </div>

        {/* ── COLUNA DIREITA: Histórico ── */}
        <div className="xl:col-span-2">
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-700 text-sm flex items-center gap-2"><Clock size={14} /> Histórico desta sessão</h2>
              {historico.length > 0 && (
                <button onClick={limparHistorico} className="text-xs text-gray-400 hover:text-red-400 transition-colors">Limpar</button>
              )}
            </div>
            {historico.length === 0 ? (
              <div className="py-12 text-center text-gray-300">
                <PhoneOff size={28} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Nenhuma ligação ainda</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50 max-h-[600px] overflow-y-auto">
                {historico.map((item, i) => <HistoricoItem key={i} item={item} />)}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Modal de Importação */}
      <ExcelImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={handleImportSuccess}
      />
    </div>
  );
}
